/**
 * GalleryCreateCard — the "summon a new genie" tile that always leads the
 * immersive /genies grid. Mirrors GalleryGenieCard's silhouette (big hero disc,
 * name, sub-line) so it sits as a natural first sibling, but wears the signature
 * gradient border to read as the one create action. Click -> /welcome, the
 * guided onboarding wizard.
 *
 * Brand law: genie tokens / signature gradient only; brand PNG icon in the disc.
 */
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { VoiceMascot } from "@/components/voice-genie/VoiceMascot";

/** Hex + alpha for the soft radial glow (alpha 0..1). */
const tintAlpha = (hex: string, a: number) =>
  hex + Math.round(a * 255).toString(16).padStart(2, "0");

export const GalleryCreateCard = () => {
  const navigate = useNavigate();
  const start = () => navigate("/welcome");

  return (
    <div
      role="button"
      tabIndex={0}
      data-hg-block="card-create"
      onClick={start}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          start();
        }
      }}
      className="group flex cursor-pointer rounded-[22px] p-[2px] bg-gradient-to-br from-[#4E9CFF] to-[#FF4DB8] shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl"
    >
      <div className="flex flex-1 flex-col items-center rounded-[20px] bg-white px-[22px] pt-[30px] pb-[26px] text-center">
        {/* hero disc — brand-tinted glow with our genie mascot, matching the genie cards */}
        <div
          className="flex items-center justify-center overflow-hidden rounded-full"
          style={{
            width: 156,
            height: 156,
            background: `radial-gradient(circle at 50% 38%, ${tintAlpha("#4E9CFF", 0.16)} 0%, ${tintAlpha("#FF4DB8", 0.1)} 70%)`,
            boxShadow: `0 0 34px ${tintAlpha("#FF4DB8", 0.18)}`,
          }}
        >
          <VoiceMascot
            mascot="onboarding-role"
            size={138}
            className="transition-transform duration-300 group-hover:scale-105"
          />
        </div>

        <h3 className="mt-[18px] text-[19px] font-extrabold tracking-[-0.3px] text-[#222B45]">
          Create a new genie
        </h3>
        <p className="mt-[3px] text-[13px] text-[#94A3B8]">
          Summon a new genie in minutes
        </p>

        <span
          className="mt-4 inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[13px] font-bold text-white shadow-md shadow-genie-primary/25 transition-all duration-200 group-hover:gap-2.5"
          style={{ backgroundImage: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)" }}
        >
          Get started
          <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
        </span>
      </div>
    </div>
  );
};

export default GalleryCreateCard;
